// Status Badge component for Green/Yellow/Red status display
import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, AlertCircle, XCircle } from 'lucide-react';

export const StatusBadge = ({ status, size = 'default', showLabel = true, animated = false }) => {
  const { t } = useLanguage();
  
  const config = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      icon: CheckCircle,
      label: t('statusGreen'),
      pulse: animated ? 'badge-pulse-success' : ''
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-700',
      icon: AlertCircle,
      label: t('statusYellow'),
      pulse: animated ? 'badge-pulse-warning' : ''
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: XCircle,
      label: t('statusRed'),
      pulse: animated ? 'badge-pulse-danger' : ''
    }
  };

  const sizeClasses = {
    small: 'px-2 py-0.5 text-xs',
    default: 'px-3 py-1 text-sm',
    large: 'px-4 py-2 text-base'
  };

  const iconSizes = {
    small: 12,
    default: 16,
    large: 20
  };

  const statusConfig = config[status] || config.green;
  const Icon = statusConfig.icon;

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full border font-semibold
        ${statusConfig.bg} ${statusConfig.border} ${statusConfig.text}
        ${sizeClasses[size]} ${statusConfig.pulse}
        transition-all duration-200
      `}
      data-testid={`status-badge-${status}`}
    >
      <Icon size={iconSizes[size]} />
      {showLabel && <span>{statusConfig.label}</span>}
    </span>
  );
};

// Score display with color coding
export const ScoreDisplay = ({ score, size = 'default' }) => {
  const getStatus = (score) => {
    if (score >= 4.0) return 'green';
    if (score >= 2.5) return 'yellow';
    return 'red';
  };

  const status = getStatus(score);
  
  const colors = {
    green: 'text-green-600',
    yellow: 'text-yellow-600',
    red: 'text-red-600'
  };

  const sizeClasses = {
    small: 'text-lg font-bold',
    default: 'text-2xl font-bold',
    large: 'text-4xl font-bold'
  };

  return (
    <span className={`${colors[status]} ${sizeClasses[size]}`} data-testid="score-display">
      {score.toFixed(1)}
    </span>
  );
};

// Score bar visualization
export const ScoreBar = ({ score, maxScore = 5, showValue = true }) => {
  const percentage = (score / maxScore) * 100;
  
  const getColor = (score) => {
    if (score >= 4.0) return 'bg-green-500';
    if (score >= 2.5) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center gap-3" data-testid="score-bar">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getColor(score)} transition-all duration-500 ease-out rounded-full`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showValue && (
        <span className="text-sm font-medium text-gray-600 min-w-[40px]">
          {score.toFixed(1)}/{maxScore}
        </span>
      )}
    </div>
  );
};

export default StatusBadge;
